import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { LinkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import MobileLayout from '../../components/MobileLayout';
import { useAuth } from '../../contexts/AuthContext';
import { clearPendingAuthorize, savePendingAuthorize } from '../../features/oauth/pendingAuthorize';

interface ConsentDetails {
  clientName: string;
  redirectHost: string;
  loopback: boolean;
  scope: string;
  paid: boolean;
}

/** Leave for the app's return address: only ever an http(s) URL the API built. */
const leaveTo = (url: unknown): boolean => {
  if (typeof url !== 'string') return false;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
  } catch {
    return false;
  }
  window.location.replace(url);
  return true;
};

/**
 * The OAuth consent page: an MCP connector (claude.ai, ChatGPT, …) asks to read and edit the
 * writer's stories. Signed out, the request is remembered until they sign in.
 */
const OAuthAuthorize: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();
  const [details, setDetails] = useState<ConsentDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);
  // A request the app got wrong: shown with a way back, never followed automatically.
  const [invalid, setInvalid] = useState<{ redirect: string; host: string } | null>(null);
  const params = useMemo(
    () => Object.fromEntries(new URLSearchParams(location.search)),
    [location.search],
  );
  // A framed consent page could be clickjacked into "Allow": refuse to work inside a frame.
  const framed = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  })();

  useEffect(() => {
    if (isLoading || framed) return;
    // Remembered until the API answers: a stored session can turn out to be dead (the 401
    // sends the tab to sign-in), and the request must survive that.
    savePendingAuthorize(location.search);
    if (!user) return;
    let cancelled = false;
    axios
      .get('/oauth/authorize', { params })
      .then((res) => {
        clearPendingAuthorize();
        if (cancelled) return;
        if (res.data?.invalid) {
          setInvalid({ redirect: res.data.redirect, host: res.data.redirectHost });
          return;
        }
        setDetails(res.data);
      })
      .catch((err) => {
        if (err?.response?.status === 401) return;
        clearPendingAuthorize();
        if (!cancelled) {
          setError(err?.response?.data?.message || t('oauth.invalid', 'This link is not valid.'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isLoading, user, framed, location.search, params, t]);

  const decide = async (approve: boolean) => {
    setBusy(true);
    try {
      const res = await axios.post('/oauth/authorize', { params, approve });
      setLeaving(true);
      if (!leaveTo(res.data?.redirect)) {
        setLeaving(false);
        setError(t('oauth.invalid', 'This link is not valid.'));
      }
    } catch (err: any) {
      if (err?.response?.data?.code === 'plan_required') {
        setDetails((d) => (d ? { ...d, paid: false } : d));
      } else {
        setError(
          err?.response?.data?.message || t('oauth.error', 'Something went wrong. Try again.'),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const card = (children: React.ReactNode) => (
    <MobileLayout showNav={false} showHeader={false}>
      <div className="flex-1 flex items-center justify-center p-6">
        <div
          className="ds-card w-full max-w-md rounded-2xl p-6 space-y-4"
          style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
        >
          <LinkIcon className="w-10 h-10 mx-auto text-primary-400" />
          {children}
        </div>
      </div>
    </MobileLayout>
  );
  const spinner = (
    <div
      className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto"
      style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
    />
  );
  const secondary = { color: 'var(--ds-text-secondary)' };
  const primary = { color: 'var(--ds-text-primary)' };

  if (framed) {
    return card(
      <p className="text-sm text-center" style={primary}>
        {t('oauth.framed', 'Open this page in its own window to continue.')}
      </p>,
    );
  }

  if (isLoading) return card(spinner);

  if (!user) {
    return card(
      <>
        <h1 className="text-xl font-semibold text-center" style={primary}>
          {t('oauth.signedOutTitle', 'An app wants to connect to Plotweave')}
        </h1>
        <p className="text-sm text-center" style={secondary}>
          {t(
            'oauth.signedOutBody',
            'Sign in to see what it is asking for. You will come straight back here.',
          )}
        </p>
        <div className="flex flex-col gap-2">
          <button
            onClick={() => navigate('/login')}
            className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold"
          >
            {t('oauth.signIn', 'Sign in')}
          </button>
          <button
            onClick={() => navigate('/signup')}
            className="w-full py-3 rounded-xl font-semibold border"
            style={{ borderColor: 'var(--ds-border)', ...primary }}
          >
            {t('oauth.signUp', 'Create an account')}
          </button>
        </div>
      </>,
    );
  }

  if (error) {
    return card(
      <>
        <p className="text-sm text-center" style={primary}>
          {error}
        </p>
        <button
          onClick={() => navigate('/stories')}
          className="w-full py-3 rounded-xl font-semibold border"
          style={{ borderColor: 'var(--ds-border)', ...primary }}
        >
          {t('oauth.toStories', 'Go to my stories')}
        </button>
      </>,
    );
  }

  if (invalid && !leaving) {
    return card(
      <>
        <p className="text-sm text-center" style={primary}>
          {t('oauth.badRequest', "This app's connection request isn't one Plotweave can accept.")}
        </p>
        <button
          onClick={() => {
            setLeaving(true);
            if (!leaveTo(invalid.redirect)) {
              setLeaving(false);
              setError(t('oauth.invalid', 'This link is not valid.'));
            }
          }}
          className="w-full py-3 rounded-xl font-semibold border"
          style={{ borderColor: 'var(--ds-border)', ...primary }}
        >
          {t('oauth.backTo', 'Back to {{host}}', { host: invalid.host })}
        </button>
        <button
          onClick={() => navigate('/stories')}
          className="w-full py-2 text-sm"
          style={{ color: 'var(--ds-text-secondary)' }}
        >
          {t('oauth.toStories', 'Go to my stories')}
        </button>
      </>,
    );
  }

  if (leaving || !details) return card(spinner);

  const name = details.clientName;
  return card(
    <>
      <h1 className="text-xl font-semibold text-center" style={primary}>
        {t('oauth.title', 'Connect {{name}} to Plotweave?', { name })}
      </h1>
      <p className="text-sm" style={secondary}>
        {t(
          'oauth.body',
          '{{name}} is asking to work with your stories as you, including stories shared with you.',
          { name },
        )}
      </p>
      <ul className="text-sm list-disc pl-5 space-y-1" style={primary}>
        <li>{t('oauth.canEdit', 'It can read and edit your stories, characters and notes.')}</li>
        <li>{t('oauth.noAi', "It can't use Plotweave's AI or spend your AI words.")}</li>
        <li>{t('oauth.noAccount', "It can't see or change your account, billing or password.")}</li>
      </ul>
      <div
        className="rounded-xl p-3 text-xs space-y-1 border"
        style={{ borderColor: 'var(--ds-border)', ...secondary }}
      >
        <div>
          {t('oauth.returnTo', 'After you answer you go back to {{host}}.', {
            host: details.redirectHost,
          })}
        </div>
        <div>
          {t(
            'oauth.unverified',
            "Plotweave hasn't checked this app: its name is what it says about itself. Only continue if you started this connection just now.",
          )}
        </div>
      </div>
      {details.loopback && (
        <div
          className="flex gap-2 text-xs rounded-xl p-3 border border-amber-400/50"
          style={primary}
        >
          <ExclamationTriangleIcon className="w-5 h-5 shrink-0 text-amber-500" />
          <span>
            {t(
              'oauth.loopback',
              'This app runs on your own computer. Any program there could be asking, so only continue if you just started connecting it yourself.',
            )}
          </span>
        </div>
      )}
      <p className="text-xs" style={secondary}>
        {t('oauth.signedInAs', 'Signed in as {{email}}.', { email: user.email })}{' '}
        {t('oauth.disconnectLater', 'You can disconnect it any time in Settings → API access.')}
      </p>
      {details.paid ? (
        <button
          onClick={() => decide(true)}
          disabled={busy}
          className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-xl font-semibold"
        >
          {t('oauth.allow', 'Allow')}
        </button>
      ) : (
        <div
          className="rounded-xl p-3 text-sm space-y-2 border"
          style={{ borderColor: 'var(--ds-border)', ...primary }}
        >
          <div>{t('oauth.planRequired', 'Connecting apps is part of every paid plan.')}</div>
          <button
            type="button"
            onClick={() => navigate('/subscription')}
            className="text-primary-400 underline"
          >
            {t('oauth.seePlans', 'See plans')}
          </button>
        </div>
      )}
      <button
        onClick={() => decide(false)}
        disabled={busy}
        className="w-full py-3 rounded-xl font-semibold border disabled:opacity-50"
        style={{ borderColor: 'var(--ds-border)', ...primary }}
      >
        {t('oauth.cancel', 'Cancel')}
      </button>
    </>,
  );
};

export default OAuthAuthorize;
